import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, PlusCircle } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export const ManualOrderModal = ({ onClose, onCreated }) => {
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [amountEdited, setAmountEdited] = useState(false);
  const [f, setF] = useState({
    customer_name: "", customer_phone: "", customer_email: "", address: "",
    suburb: "", water_type: "town", loads: 1, scheduled_date: todayISO(),
    payment_method: "cod", amount: "", notes: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/service/config").then(({ data }) => setZones(data.zones || [])).catch(() => {});
  }, []);

  const zone = zones.find((z) => z.name === f.suburb);
  const perLoad = zone ? (f.water_type === "pure" ? zone.pure_water : zone.town_water) : null;

  useEffect(() => {
    if (!amountEdited && perLoad != null) {
      set("amount", String(Math.round(perLoad * f.loads * 100) / 100));
    }
  }, [f.suburb, f.water_type, f.loads, perLoad, amountEdited]);

  const submit = async () => {
    if (!f.customer_name.trim() || !f.suburb) { toast.error("Enter customer name and suburb"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/staff/orders", {
        ...f, loads: Number(f.loads),
        amount: f.amount === "" ? null : Number(f.amount),
      });
      onCreated?.(data);
      toast.success("Manual load created");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSaving(false);
    }
  };

  const Fld = ({ label, k, type = "text", placeholder, required }) => (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1 block">{label}{required && <span className="text-red-500"> *</span>}</label>
      <input type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder}
        data-testid={`manual-${k}`}
        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()} data-testid="manual-order-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><PlusCircle className="w-5 h-5 text-blue-600" /> New Manual Load</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Fld label="Customer Name" k="customer_name" required placeholder="e.g. John Smith" />
          <Fld label="Phone" k="customer_phone" placeholder="021..." />
          <Fld label="Email (optional)" k="customer_email" type="email" placeholder="Sends a confirmation" />
          <Fld label="Address" k="address" placeholder="Street address" />
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Suburb <span className="text-red-500">*</span></label>
            <select value={f.suburb} onChange={(e) => set("suburb", e.target.value)} data-testid="manual-suburb"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="">Select suburb…</option>
              {zones.map((z) => <option key={z.name} value={z.name}>{z.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Water Type</label>
            <select value={f.water_type} onChange={(e) => set("water_type", e.target.value)} data-testid="manual-water_type"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="town">Town Water</option>
              <option value="pure">Pure Water</option>
            </select>
          </div>
          <Fld label="Loads" k="loads" type="number" />
          <Fld label="Delivery Date" k="scheduled_date" type="date" />
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Payment</label>
            <select value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="manual-payment_method"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="cod">Cash on Delivery</option>
              <option value="invoice">To be Invoiced</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Amount ($)</label>
            <input type="number" value={f.amount} onChange={(e) => { setAmountEdited(true); set("amount", e.target.value); }}
              data-testid="manual-amount"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
          <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="manual-notes"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
        </div>
        <button onClick={submit} disabled={saving} data-testid="manual-submit-btn"
          className="brand-gradient btn-shine text-white font-semibold w-full py-3 rounded-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><PlusCircle className="w-5 h-5" /> Create Load</>}
        </button>
      </div>
    </div>
  );
};
